import type { RoomOverviewRow } from '@repo/sdk/gen'
import { zRoomOverviewForResidenceData } from '@repo/sdk/zod'
import { Badge } from '@repo/ui/badge'
import { DataTableColumnSettings } from '@repo/ui/components/data-table/column-settings'
import {
	DataTable,
	useDataTableColumnState
} from '@repo/ui/components/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/components/data-table/types'
import { InlineFilterSelect } from '@repo/ui/components/inline-filters/inline-filter-options'
import { InlineFilters } from '@repo/ui/components/inline-filters/inline-filters'
import { InlineFiltersReset } from '@repo/ui/components/inline-filters/inline-filters-reset'
import { ListInputSearch } from '@repo/ui/components/list-input-search'
import { ListLayout } from '@repo/ui/components/list-layout'
import { Skeleton } from '@repo/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import {
	createFileRoute,
	Link,
	stripSearchParams,
	useNavigate
} from '@tanstack/react-router'
import { Accessibility } from 'lucide-react'
import { z } from 'zod'
import { useTranslation } from '@/lib/i18n'
import { usePageSize } from '@/stores/use-page-size'
import { WhenWritable } from '../../-components/when-writable'
import { useWorkingCall } from '../../-hooks/use-working-call'
import { formatDay } from '../../-lib/rome-time'
import { VERIFICATION_ITEM_LABEL } from '../verifiche/-lib/labels'
import { ArrivalActions } from './-components/arrival-actions'
import { RoomCounts } from './-components/room-counts'
import { roomOverviewQueryOptions } from './-query/query-options'

const filterShape = zRoomOverviewForResidenceData.shape.body.shape.filters.shape
const stateFilter = filterShape.state
const verificationFilter = filterShape.verificationState

const searchSchema = z.object({
	state: stateFilter,
	verification: verificationFilter,
	search: z.string().trim().default(''),
	page: z.number().int().min(1).default(1)
})

const STATES = stateFilter.unwrap().options
const VERIFICATION_STATES = verificationFilter.unwrap().options

const EMPTY_ROWS: (RoomOverviewRow & { id: string })[] = []

export const Route = createFileRoute('/(private)/bando/$residenceId/ingressi/')(
	{
		component: ArrivalsPage,
		validateSearch: searchSchema,
		search: {
			middlewares: [stripSearchParams(searchSchema.parse({}))]
		},
		staticData: {
			breadcrumb: 'admin-arrivals:page_title'
		}
	}
)

/**
 * The assignees who accepted, one row each, from the acceptance to the moving
 * in (FFD 2.13 and 2.14): the preference of room, the verification with what is
 * still to check, the room proposed and the moving in, a page at a time, with
 * the beds of each quota and site above (always of every assignee), so the
 * limits are in view before proposing. The name opens the card of the
 * verification; each row has its commands in one menu.
 */
function ArrivalsPage() {
	const { t, i18n } = useTranslation()
	const { residenceId } = Route.useParams()
	const { state, verification, search, page } = Route.useSearch()
	const navigate = useNavigate({ from: Route.fullPath })
	const pageSize = usePageSize((s) => s.pageSize)
	const setPageSize = usePageSize((s) => s.setPageSize)
	const { callId } = useWorkingCall()
	const query = useQuery(
		roomOverviewQueryOptions({
			page,
			limit: pageSize,
			filters: {
				residenceId,
				callId,
				state,
				verificationState: verification,
				search: search || undefined
			}
		})
	)
	const quotas = query.data?.quotas ?? []
	const sites = query.data?.sites ?? []
	const causes = query.data?.causes ?? []

	const columns: DataTableColumn<RoomOverviewRow & { id: string }>[] = [
		{
			id: 'protocol',
			header: t('admin-arrivals:columns.protocol'),
			cell: (row) => <span className='tabular-nums'>{row.protocol}</span>,
			// Five characters, a dash and five more: wide letters like M and W need the room.
			minWidth: 120,
			width: 140,
			pinned: 'left'
		},
		{
			id: 'name',
			header: t('admin-arrivals:columns.name'),
			cell: (row) => <Student row={row} />,
			minWidth: 180,
			width: 220,
			pinned: 'left'
		},
		{
			id: 'quota',
			header: t('admin-arrivals:columns.quota'),
			cell: (row) => t(`admin-arrivals:quota.${row.quota}`),
			minWidth: 110,
			flex: 1
		},
		{
			id: 'preference',
			header: t('admin-arrivals:columns.preference'),
			cell: (row) => t(`admin-arrivals:preference.${row.roomPreference}`),
			minWidth: 130,
			flex: 1
		},
		{
			id: 'verification',
			header: t('admin-arrivals:columns.verification'),
			cell: (row) => <Verification row={row} />,
			minWidth: 260,
			flex: 2
		},
		{
			id: 'room',
			header: t('admin-arrivals:columns.room'),
			cell: (row) => <Room row={row} />,
			minWidth: 240,
			flex: 2
		},
		{
			id: 'check_in',
			header: t('admin-arrivals:columns.check_in'),
			cell: (row) =>
				row.assignment?.checkInDate ? (
					<span className='tabular-nums'>
						{formatDay(row.assignment.checkInDate, i18n.language)}
					</span>
				) : (
					'-'
				),
			minWidth: 120,
			flex: 1
		},
		{
			id: 'actions',
			header: (
				<span className='sr-only'>
					{t('admin-arrivals:columns.actions')}
				</span>
			),
			cell: (row) => (
				<WhenWritable>
					<ArrivalActions
						causes={causes}
						quotas={quotas}
						residenceId={residenceId}
						row={row}
						sites={sites}
					/>
				</WhenWritable>
			),
			width: 56,
			minWidth: 56,
			pinned: 'right',
			manageable: false,
			enableColumnMenu: false
		}
	]
	const columnsState = useDataTableColumnState(columns)
	const rows = query.data?.data.map((row) => ({ ...row, id: row.offerId }))

	return (
		<ListLayout
			listSlot={
				<div className='flex min-h-0 flex-1 flex-col gap-4'>
					<p className='text-muted-foreground text-sm'>
						{t('admin-arrivals:intro')}
					</p>
					{query.isLoading ? (
						<Skeleton className='h-28 w-full' />
					) : (
						<RoomCounts quotas={quotas} sites={sites} />
					)}
					<div className='flex flex-col items-stretch gap-2 md:flex-row md:flex-wrap md:items-center'>
						<ListInputSearch
							placeholder={t('admin-arrivals:search_placeholder')}
							resetKeys={['page']}
						/>
						<InlineFilters>
							<InlineFilterSelect
								label={t('admin-arrivals:filter')}
								onValueChange={(next) =>
									navigate({
										search: (prev) => ({
											...prev,
											state: STATES.find(
												(value) => value === next
											),
											page: 1
										}),
										replace: true
									})
								}
								options={STATES.map((value) => ({
									label: t(`admin-arrivals:state.${value}`),
									value
								}))}
								value={state}
							/>
							<InlineFilterSelect
								label={t('admin-arrivals:verification_filter')}
								onValueChange={(next) =>
									navigate({
										search: (prev) => ({
											...prev,
											verification:
												VERIFICATION_STATES.find(
													(value) => value === next
												),
											page: 1
										}),
										replace: true
									})
								}
								options={VERIFICATION_STATES.map((value) => ({
									label: t(
										`admin-arrivals:verification.${value}`
									),
									value
								}))}
								value={verification}
							/>
							{(state || verification) && (
								<InlineFiltersReset
									onReset={() =>
										navigate({
											search: (prev) => ({
												...prev,
												state: undefined,
												verification: undefined,
												page: 1
											}),
											replace: true
										})
									}
								/>
							)}
						</InlineFilters>
						<DataTableColumnSettings
							columns={columns}
							columnsState={columnsState}
						/>
					</div>
					<DataTable
						columns={columns}
						columnsState={columnsState}
						data={rows ?? EMPTY_ROWS}
						onRowClick={(row) => {
							// A seat accepted before verifications existed has no card to open.
							if (row.verificationId) {
								navigate({
									params: {
										residenceId,
										verificationId: row.verificationId
									},
									search: {
										state: verification,
										search: search || undefined
									},
									to: '/bando/$residenceId/verifiche/$verificationId'
								})
							}
						}}
						pagination={{
							pageIndex: page - 1,
							pageCount: query.data?.meta.totalPages ?? 1,
							onPageChange: (next) =>
								navigate({
									search: (prev) => ({
										...prev,
										page: next + 1
									}),
									replace: true
								}),
							pageSize,
							onPageSizeChange: (next) => {
								setPageSize(next)
								navigate({
									search: (prev) => ({ ...prev, page: 1 }),
									replace: true
								})
							}
						}}
						state={{
							loading: query.isPending || query.isPlaceholderData,
							error: query.isError && t('admin-arrivals:error'),
							empty:
								state || verification || search
									? t('admin-arrivals:empty_filtered')
									: t('admin-arrivals:empty')
						}}
					/>
				</div>
			}
			title={t('admin-arrivals:page_title')}
		/>
	)
}

/** The name opens the card of the verification, with the filters of the list for its arrows; the equipped seat of a disability sits beside it. */
function Student({ row }: { row: RoomOverviewRow }) {
	const { t } = useTranslation()
	const { residenceId } = Route.useParams()
	const { verification, search } = Route.useSearch()
	const name =
		[row.lastName, row.firstName].filter(Boolean).join(' ') ||
		t('admin-arrivals:not_given')
	return (
		<span className='flex min-w-0 items-center gap-1.5'>
			{row.verificationId ? (
				<Link
					className='truncate font-medium hover:underline'
					params={{ residenceId, verificationId: row.verificationId }}
					search={{
						state: verification,
						search: search || undefined
					}}
					title={name}
					to='/bando/$residenceId/verifiche/$verificationId'
				>
					{name}
				</Link>
			) : (
				<span className='truncate font-medium' title={name}>
					{name}
				</span>
			)}
			{row.seatOrigin === 'equipped_priority' && (
				<Accessibility
					aria-label={t('admin-arrivals:equipped_priority')}
					className='size-3.5 shrink-0 text-muted-foreground'
					role='img'
				/>
			)}
		</span>
	)
}

/** The state of the verification, and while it waits the items still to check and the student number when it is missing. */
function Verification({ row }: { row: RoomOverviewRow }) {
	const { t } = useTranslation()
	const toCheck = [
		...row.itemsToCheck.map((item) => t(VERIFICATION_ITEM_LABEL[item])),
		...(row.studentNumberMissing
			? [t('admin-verifications:to_check.student_number')]
			: [])
	].join(', ')
	return (
		<div className='flex min-w-0 flex-col items-start gap-0.5'>
			<Badge
				variant={VERIFICATION_TONE[row.verificationState ?? 'pending']}
			>
				{t(
					`admin-arrivals:verification.${row.verificationState ?? 'none'}`
				)}
			</Badge>
			{toCheck && (
				<span
					className='block max-w-full truncate text-muted-foreground text-xs'
					title={toCheck}
				>
					{t('admin-arrivals:verification.to_check', {
						items: toCheck
					})}
				</span>
			)}
		</div>
	)
}

/** The room and the site proposed, equipped or not; or the seat lost, with its cause and day. One line, whole on hover. */
function Room({ row }: { row: RoomOverviewRow }) {
	const { t, i18n } = useTranslation()
	const { assignment, forfeit } = row
	if (forfeit) {
		const text = `${t('admin-arrivals:state.forfeited')}: ${
			forfeit.factDate
				? t('admin-arrivals:fact.line', {
						cause: forfeit.causeText,
						date: formatDay(forfeit.factDate, i18n.language)
					})
				: forfeit.causeText
		}`
		return (
			<span className='block truncate text-destructive' title={text}>
				{text}
			</span>
		)
	}
	if (!assignment) {
		return (
			<span className='text-muted-foreground'>
				{t('admin-arrivals:room.none')}
			</span>
		)
	}
	const text = [
		`${t(`admin-arrivals:room.${assignment.roomType}`)}${
			assignment.equipped ? `, ${t('admin-arrivals:room.equipped')}` : ''
		}`,
		assignment.siteName
	]
		.filter(Boolean)
		.join(' · ')
	return (
		<span className='block truncate' title={text}>
			{text}
		</span>
	)
}

const VERIFICATION_TONE = {
	pending: 'warning',
	compliant: 'success',
	excluded: 'destructive'
} as const satisfies Record<
	NonNullable<RoomOverviewRow['verificationState']>,
	string
>
